import { Feedstock, FeedstockPropName } from '../../../../models/project/feedstock/feedstock.model';
import { Project } from '../../../../models/project/project.model';
import { ProjectValidationUnit } from '../../../../models/data-structures/project-validation';
import { FeedstockQuantitiesValidation } from './feedstock-quantities.validation';
import { ValidationInterface } from '../../validation.interface';
import { FeedstockCharacteristicsValidation } from './feedstock-characteristics.validation';
import { FeedstockBmpPotentialValidation } from './feedstock-bmp-validation/feedstock-bmp-potential.validation';
import { FeedstockNpkValidation } from './feedstock-npk.validation';
import { FinancialFeedstockValidation } from '../financial-validation/financial-feedstock.validation';

export class FeedstockValidation implements ValidationInterface {
  /** The current feedstock obj that is being validated */
  feedstock: Feedstock;

  /**
   * Sets the feedstock obj that will be used for validation
   * @param feedstock will be used to set the current feedstock obj
   * @param isProjectInput is a boolean that determines if a project input form
   * is being validated to make some inputs optional
   */
  constructor(feedstock: Feedstock, private isProjectInput?: boolean) {
    if (!feedstock) {
      throw Error('unable to validate feedstock');
    }
    this.feedstock = new Feedstock(feedstock);
  }

  /**
   * Get a validation unit containing the validation data for the current feedstock obj
   */
  getValidation(vu?: ProjectValidationUnit, project?: Project) {
    const validationUnit = new ProjectValidationUnit();
    if (project) {
      this.addBasicInfoValidation(validationUnit, project);
      this.addQuantitiesValidation(validationUnit);
      this.addCharacteristicsValidation(validationUnit);
      if (!this.isProjectInput || this.feedstock.characteristics.characteristicsEnabled) {
        this.addBmpPotentialValidation(validationUnit);
      }
      if (!this.isProjectInput) {
        this.addNpkValidation(validationUnit);
      }
    }

    return validationUnit;
  }

  /**
   * returns a boolean that determines if the current feedstock obj is valid
   */
  isValid(): boolean {
    return this.getValidation().isValid;
  }

  /**
   * Determines if the feedstock has the inputs needed for opex expense calculation (non transport).
   */
  isValidForOpexExpenseNonTransport(): boolean {
    let valid = true;
    if (this.feedstock) {
      valid = valid && new FeedstockQuantitiesValidation(this.feedstock.quantities).isValid();
      valid = valid && new FinancialFeedstockValidation(this.feedstock.financial).validForOpexNonTransport();
    } else {
      valid = false;
    }
    return valid;
  }

  /**
   * Determines if the feedstock has the transportation inputs needed for the expense calculation.
   */
  isValidForOpexExpenseTransport(): boolean {
    let valid = true;
    if (this.feedstock) {
      valid = valid && new FeedstockQuantitiesValidation(this.feedstock.quantities).isValid();
      valid = valid && new FinancialFeedstockValidation(this.feedstock.financial).validForOpexTransportExpense();
    } else {
      valid = false;
    }
    return valid;
  }

  /**
   * Adds validation info for feedstock name, category, type, and source
   * @param validationUnit is the validation unit for which the basic feedstock validation will be added to
   */
  private addBasicInfoValidation(validationUnit: ProjectValidationUnit, project: Project): void {
    if (this.feedstock.name) {
      validationUnit.setValid(FeedstockPropName.name);
    } else {
      validationUnit.setInvalid(FeedstockPropName.name, [FeedstockError.MISSING_NAME]);
    }
    let wasteDisposalEnabled = false;
    if (project && project.ghgEmissions && project.projectInfo.useGHG && project.ghgEmissions.includeWasteDisposal) {
      wasteDisposalEnabled = true;
    }
    // only require all dropdown data when category is selected or current waste disposal situation is enabled
    if (this.feedstock.category || wasteDisposalEnabled) {
      if (this.feedstock.category) {
        validationUnit.setValid(FeedstockPropName.category);
      } else {
        validationUnit.setInvalid(FeedstockPropName.category, [FeedstockError.MISSING_CATEGORY]);
      }
      if (this.feedstock.type) {
        validationUnit.setValid(FeedstockPropName.type);
      } else {
        validationUnit.setInvalid(FeedstockPropName.type, [FeedstockError.MISSING_TYPE]);
      }
      if (this.feedstock.source) {
        validationUnit.setValid(FeedstockPropName.source);
      } else {
        validationUnit.setInvalid(FeedstockPropName.source, [FeedstockError.MISSING_SOURCE]);
      }
    }
  }

  /**
   * Adds validation for feedstock quantities
   * @param validationUnit is the validation unit for which the feedstock quantities validation will be added to
   */
  private addQuantitiesValidation(validationUnit: ProjectValidationUnit): void {
    validationUnit.addModuleResults(
      new FeedstockQuantitiesValidation(this.feedstock.quantities).getValidation(),
      FeedstockPropName.quantities
    );
  }

  /**
   * Adds validation for feedstock characteristics
   * @param validationUnit is the validation unit for which the feedstock characteristics validation will be added to
   */
  private addCharacteristicsValidation(validationUnit: ProjectValidationUnit): void {
    if (this.feedstock?.characteristics?.characteristicsEnabled || !this.isProjectInput) {
      validationUnit.addModuleResults(
        new FeedstockCharacteristicsValidation(this.feedstock.characteristics, this.useDatabase()).getValidation(),
        FeedstockPropName.characteristics
      );
    }
  }

  /**
   * Adds validation for feedstock BMP potential
   * @param validationUnit is the validation unit for which the feedstock BMP potential validation will be added to
   */
  private addBmpPotentialValidation(validationUnit: ProjectValidationUnit): void {
    if (!this.useDatabase() || (this.feedstock && this.feedstock.bmpPotential)) {
      validationUnit.addModuleResults(
        new FeedstockBmpPotentialValidation(this.feedstock.bmpPotential, this.useDatabase()).getValidation(),
        FeedstockPropName.bmpPotential
      );
    }
  }

  /**
   * Adds validation for feedstock NPK
   * @param validationUnit is the validation unit for which the feedstock NPK validation will be added to
   */
  private addNpkValidation(validationUnit: ProjectValidationUnit): void {
    if (this.feedstock.npkEnabled) {
      validationUnit.addModuleResults(
        new FeedstockNpkValidation(this.feedstock.npk).getValidation(),
        FeedstockPropName.npk
      );
    }
  }

  /**
   * Get a boolean that determines if the current feedstock being validated is using default database
   * values. This will determine what inputs are optional.
   */
  private useDatabase(): boolean {
    // DON'T SIMPLIFY - some stuff doesn't act right if this doesn't
    // return a boolean
    if (this.feedstock.category &&
      (this.feedstock.category !== 'unknown' && this.feedstock.type !== 'unknown'
        && this.feedstock.source !== 'unknown')) {
      return true;
    }
    return false;
  }
}

/**
 * An enum that contains formatted error messages that can occur during feedstock validation
 */
enum FeedstockError {
  MISSING_NAME = 'VALIDATION.MISSING_NAME',
  MISSING_CATEGORY = 'VALIDATION.MISSING_CATEGORY',
  MISSING_TYPE = 'VALIDATION.MISSING_TYPE',
  MISSING_SOURCE = 'VALIDATION.MISSING_SOURCE'
}
